import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchAPI, getFromCache, save2Cache, applyTimeZone, getSubdomain } from '../../../utils/utils';
import moment from 'moment';
import { calculateProvesHores } from '../proves/LlistaProves';
import { formatDurations } from '../ProvesApp';
import ProvaTitle from './parts/ProvaTitle';
import NomsPisos from './parts/NomsPisos';
import Rengla from './parts/Rengla';
import CastellersList from './parts/CastellersList';
import MissatgesProva from './parts/MissatgesProva';
import TroncSkeleton from './parts/TroncSkeleton';
import { getCastellerSearchText, getCastellerShortName } from '../../../utils/castellerName';

// Create a memoized casteller item component
const CastellerItem = React.memo(({ assist, isSelected, onClick, opacity }) => (
    <div
        className={`casteller-item ${isSelected ? 'selected' : ''}`}
        onClick={onClick}
        style={{
            color: assist['assistència'] === 2 ? 'darkblue' :
                   assist['assistència'] === 1 ? 'green' :
                   'darkorange',
            opacity
        }}
    >
        {getCastellerShortName(assist)}
    </div>
));

function WebAppTroncs({ event, socket, castellersInfo, bundlesInfo, userInfo }) {
    const [selectedProves, setSelectedProves] = useState({
        'public': [],
        'private': [],
        'admin': [],
    });
    const [updateProves, setUpdateProves] = useState({});
    const [assistencies, setAssistencies] = useState([]);
    const [selectedBoxes, setSelectedBoxes] = useState({});
    const [selectedCasteller, setSelectedCasteller] = useState(null);
    const [selectedTronc, setSelectedTronc] = useState(null);
    const [troncs, setTroncs] = useState({});
    const [order, setOrder] = useState(false);
    const [eventInfo, setEventInfo] = useState(null);
    const [durations, setDurations] = useState(null);
    const [horaInici, setHoraInici] = useState(null);
    const [provesHores, setProvesHores] = useState(null);
    const [bundleJSONs, setBundleJSONs] = useState({});
    const [lastPairs, setLastPairs] = useState({});
    const [currentCastellersInTronc, setCurrentCastellersInTronc] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [etiquetes, setEtiquetes] = useState([]);
    const [etiquetaUsers, setEtiquetaUsers] = useState({});
    const [isExporting, setIsExporting] = useState(false);
    const [exportUrl, setExportUrl] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Check if the device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        // Initial check
        checkIfMobile();
        
        // Add event listener for window resize
        window.addEventListener('resize', checkIfMobile);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Load event info and assistencies
    useEffect(() => {
        if (!event) return;
        
        fetchAPI(`/event/${event}`, data => setEventInfo(data));
        fetchAPI(`/proves/${event}`, data => {
            setSelectedProves(data);
            // Load tronc data for each prova
            const allProves = [...data.public, ...data.private, ...data.admin];
            allProves.forEach(filename => {
                const [castell, versio] = filename.split('.');
                fetchAPI(`/events/${event}/renglesambfustesicolumnesambsim/${castell}.${versio}`, data => {
                    setTroncs(prev => ({
                        ...prev,
                        [event]: {
                            ...prev[event],
                            [versio]: data
                        }
                    }));
                }, false);
            });
        });
        fetchAPI(`/order/${event}`, data => setOrder(data));
        fetchAPI(`/all_assistencies_event/${event}`, data => setAssistencies(data.data));
        fetchAPI(`/horainici/${event}`, res => res ? setHoraInici(moment(res)) : setHoraInici(applyTimeZone()));
        fetchAPI('/durations', res => setDurations(formatDurations(res)));
    }, [event]);

    // Calculate proves hores when dependencies change
    useEffect(() => {
        if (!selectedProves || !order || !durations || !horaInici) return;
        const hores = calculateProvesHores(selectedProves, order, durations, horaInici);
        setProvesHores(hores);
    }, [selectedProves, order, durations, horaInici]);

    // Keep currentCastellersInTronc in sync with actual troncs data
    useEffect(() => {
        if (!selectedTronc || !troncs[event]?.[selectedTronc]) {
            return;
        }
        const currentTronc = troncs[event][selectedTronc];
        const castellersInTronc = currentTronc
            ?.flat()
            ?.filter(([caixa, casteller]) => casteller && !caixa.startsWith('nom') && !caixa.startsWith('fusta'))
            ?.map(([_, casteller]) => parseInt(casteller)) || [];
        setCurrentCastellersInTronc(castellersInTronc);
    }, [troncs, selectedTronc, event]);

    // Handle socket events for real-time updates
    useEffect(() => {
        if (!socket || !event) return;

        const handleChangedStateProva = (evId, castell, versio, old_state, new_state) => {
            if (parseInt(event) === parseInt(evId) && old_state !== new_state) {
                setSelectedProves(prev => {
                    if (old_state in prev) {
                        const newState = {
                            ...prev,
                            [old_state]: prev[old_state].filter(p => p !== `${castell}.${versio}.canvis`),
                        };
                        if (new_state in newState) {
                            newState[new_state] = [...newState[new_state], `${castell}.${versio}.canvis`];
                        }
                        return newState;
                    }
                    return prev;
                });
            }
        };

        const handlePosicionsChanged = (eventId, castell, versio, pos) => {
            if (parseInt(event) !== parseInt(eventId)) return;
            setUpdateProves(prev => ({
                ...prev,
                [`${castell}.${versio}`]: pos
            }));
        };

        socket.on('.changed_state_prova', handleChangedStateProva);
        socket.on('.posicions_changed', handlePosicionsChanged);

        return () => {
            socket.off('.changed_state_prova', handleChangedStateProva);
            socket.off('.posicions_changed', handlePosicionsChanged);
        };
    }, [socket, event]);

    // Load bundle JSONs
    useEffect(() => {
        if (!event || !selectedProves) return;

        const loadBundleJSON = async (castell) => {
            // Check cache first
            const cachedJSON = await getFromCache(`bundle_json.${castell}`);
            if (cachedJSON) {
                setBundleJSONs(prev => ({ ...prev, [castell]: cachedJSON }));
                return;
            }

            // Fetch if not in cache
            fetchAPI(`/bundle_link/${castell}`, ({ id, link }) => {
                if (id === castell) {
                    fetch(link)
                        .then(res => res.json())
                        .then(json => {
                            setBundleJSONs(prev => ({ ...prev, [castell]: json }));
                            save2Cache(`bundle_json.${castell}`, json);
                        })
                        .catch(e => console.error('Error loading bundle:', e));
                }
            });
        };

        // Load bundle JSON for each castell
        const allProves = [
            ...selectedProves.public,
            ...selectedProves.private,
            ...selectedProves.admin
        ];

        allProves.forEach(filename => {
            const [castell] = filename.split('.');
            if (!bundleJSONs[castell]) {
                loadBundleJSON(castell);
            }
        });
    }, [event, selectedProves]);

    // Compute proves from selectedProves and order
    const allProves = [
        ...selectedProves.public,
        ...selectedProves.private,
        ...selectedProves.admin
    ];

    const orderedProves = order && selectedProves
        ? [
            ...order.filter(castell => allProves.includes(castell)),
            ...allProves.filter(castell => !order.includes(castell))
        ]
        : allProves;

    const proves = orderedProves
        .map(filename => filename.split('.'))
        .map(([castell, versio, _]) => ({ event: event, castell: castell, versio: versio }));

    // New function to apply casteller changes, copying mobileapp logic
    const applyChangeNow = (prova, pos) => {
        const [changedBox, changedCasteller] = pos.split(',');
        const isRemove = changedCasteller === '_EMPTY_';
        setTroncs(prev => {
            const newTroncs = {
                ...prev,
                [prova.event]: {
                    ...prev[prova.event],
                    [prova.versio]: prev[prova.event]?.[prova.versio]?.map(rengla =>
                        rengla.map(([caixa, casteller]) => {
                            if (parseInt(casteller) === parseInt(changedCasteller) && caixa !== changedBox && !isRemove) {
                                return [caixa, null];
                            } else if (caixa === changedBox && !isRemove) {
                                return [caixa, changedCasteller];
                            } else if (caixa === changedBox && isRemove) {
                                return [caixa, null];
                            }
                            return [caixa, casteller];
                        })
                    )
                }
            };

            return newTroncs;
        });
    };

    // Updated handleBoxSelect
    const handleBoxSelect = (prova, box) => {
        setSelectedTronc(prova.versio);
        setSelectedBoxes(prev => ({ ...prev, [prova.versio]: box }));
    };

    // Updated handleCastellerSelect now uses applyChangeNow
    const handleCastellerSelect = (castellerId) => {
        if (!selectedTronc || !selectedBoxes[selectedTronc]) return;
        const currentProva = proves.find(p => p.versio === selectedTronc);
        if (!currentProva) return;

        // Generate a random action_id like in mobile app
        const action_id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        
        const isRemove = castellerId === null;
        const pos = `${selectedBoxes[selectedTronc]},${isRemove ? '_EMPTY_' : castellerId}`;
        
        // Apply change locally
        applyChangeNow(currentProva, pos);

        // Save last pair for UX
        setLastPairs(prev => ({
            ...prev,
            [selectedTronc]: { caixa: selectedBoxes[selectedTronc], casteller: castellerId }
        }));

        // Emit socket events
        socket.emit('.save_change_to_room', 
            `${currentProva.event}.${currentProva.castell}.${currentProva.versio}`,
            pos,
            action_id
        );
        socket.emit('.update_posicions', event, selectedTronc, castellerId);
        
        setSelectedCasteller(null);
    };

    // Add swap functionality
    const handleSwap = () => {
        if (!selectedTronc || !selectedBoxes[selectedTronc]) return;
        const currentProva = proves.find(p => p.versio === selectedTronc);
        if (!currentProva) return;
        
        const lastPair = lastPairs[selectedTronc];
        if (!lastPair) return;

        // Get current box's casteller
        const currentCasteller = troncs[event]?.[selectedTronc]
            ?.flat()
            ?.find(([caixa, _]) => caixa === selectedBoxes[selectedTronc])?.[1];

        // Generate a single action_id for both changes
        const action_id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        // Apply first change (move current casteller to last box)
        const pos1 = `${lastPair.caixa},${currentCasteller || '_EMPTY_'}`;
        applyChangeNow(currentProva, pos1);
        socket.emit('.save_change_to_room', 
            `${currentProva.event}.${currentProva.castell}.${currentProva.versio}`,
            pos1,
            action_id
        );

        // Apply second change (move last casteller to current box)
        const pos2 = `${selectedBoxes[selectedTronc]},${lastPair.casteller || '_EMPTY_'}`;
        applyChangeNow(currentProva, pos2);
        socket.emit('.save_change_to_room',
            `${currentProva.event}.${currentProva.castell}.${currentProva.versio}`,
            pos2,
            action_id
        );

        // Clear all selections and swappable state after swap
        setSelectedBoxes({});
        setSelectedTronc(null);
        setLastPairs({});
        setSelectedCasteller(null);
    };

    // Check if swap is possible
    const canSwap = (selectedTronc && selectedBoxes[selectedTronc] && 
        lastPairs[selectedTronc]?.caixa && 
        lastPairs[selectedTronc]?.caixa !== selectedBoxes[selectedTronc] && 
        lastPairs[selectedTronc]?.casteller !== null &&
        // Check that current box has a casteller
        troncs[event]?.[selectedTronc]
            ?.flat()
            ?.find(([caixa, casteller]) => caixa === selectedBoxes[selectedTronc])?.[1] !== null);

    // Updated handleOutsideClick
    const handleOutsideClick = (e) => {
        if (e.target.closest('.cl-panel')) return;
        if (e.target.closest('.box')) return;

        setSelectedBoxes({});
        setSelectedTronc(null);
        setLastPairs({});
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 150);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Add text normalization function
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[^a-z0-9]/g, ''); // Remove special characters and spaces
    };

    // Memoize filtered assistencies with normalized search
    const filteredAssistencies = useMemo(() => {
        return assistencies
            .filter(assist => assist.casteller && !assist.lesionat && !assist.hidden)
            .filter(assist => assist['assistència'] === 1 || assist['assistència'] === 2)
            .filter(assist => {
                if (!debouncedSearchQuery) return true;
                const searchNormalized = normalizeText(debouncedSearchQuery);
                const fullName = normalizeText(getCastellerSearchText(assist));
                return fullName.includes(searchNormalized);
            });
    }, [assistencies, debouncedSearchQuery]);

    // Memoize the check for casteller in box
    const hasCastellerInSelectedBox = useMemo(() => {
        if (!selectedTronc || !selectedBoxes[selectedTronc]) return false;
        return troncs[event]?.[selectedTronc]
            ?.flat()
            ?.find(([caixa, casteller]) => caixa === selectedBoxes[selectedTronc])?.[1] !== null;
    }, [selectedTronc, selectedBoxes, troncs, event]);

    // Memoize handlers
    const handleCastellerClick = useCallback((castellerId) => {
        handleCastellerSelect(castellerId);
        setSearchQuery(''); // Clear search input after selection
    }, [handleCastellerSelect]);

    // Fetch etiquetes
    useEffect(() => {
        fetchAPI('/etiquetes', setEtiquetes);
    }, []);

    // Fetch etiqueta users when etiquetes change
    useEffect(() => {
        etiquetes.forEach(etiqueta => {
            fetchAPI(
                `/etiqueta_users/${etiqueta.id}`,
                users => setEtiquetaUsers(
                    prev => ({
                        ...prev,
                        [etiqueta.id]: {
                            castellers: users,
                            perfil: etiqueta.nom
                        }
                    })
                )
            );
        });
    }, [etiquetes]);

    // Handle export to Google Sheets
    const handleExportTroncs = () => {
        if (!event) return;
        
        // Use orderedProves to maintain the same order as displayed in the UI
        const provesForExport = orderedProves
            .map(filename => filename.split('.').slice(0, 2).join('.')); // Get only 'castell.versio' format
        
        const provesParam = provesForExport.join(',');
        
        // Show loading state
        setIsExporting(true);
        
        // Call the export API using fetchAPI
        fetchAPI(`/export_troncs/${event}?proves=${provesParam}`, 
            data => {
                if (data.url) {
                    setExportUrl(data.url);

                    // Try to open the URL in a new tab
                    window.open(data.url, '_blank');
                } else {
                    console.error('Export failed, no URL returned');
                    alert('No s\'ha pogut crear el document de Google Sheets');
                    setExportUrl(null);
                }
                setIsExporting(false);
            },
            false
        );
    };

    // Function to handle manual opening of the export URL
    const handleManualOpen = () => {
        if (exportUrl) {
            window.open(exportUrl, '_blank');
        }
    };

    // Function to copy the URL to clipboard
    const handleCopyUrl = () => {
        if (exportUrl) {
            navigator.clipboard.writeText(exportUrl)
                .then(() => {
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                })
                .catch(err => {
                    console.error('Failed to copy URL: ', err);
                    alert('No s\'ha pogut copiar l\'enllaç. Si us plau, copieu-lo manualment.');
                });
        }
    };

    return (
        <div 
            className={`web-app-troncs ${isMobile ? 'mobile' : ''}`}
            onClick={handleOutsideClick}
        >
            <div className="troncs-container">
                <div className="troncs-list">
                    {eventInfo && (
                        <div className="event-info">
                            <div className="event-info-content">
                                <h1>{eventInfo.title}</h1>
                                <div className="event-datetime">
                                    {moment(eventInfo['data-esperada-inici']).format('DD/MM/YYYY HH:mm')}
                                </div>
                                <div className="event-location">
                                    {eventInfo.lloc || 'Lloc no especificat'}
                                </div>
                            </div>
                            <div className="event-buttons">
                                <button 
                                    className="export-button" 
                                    onClick={handleExportTroncs}
                                    disabled={isExporting}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" className="sheets-icon">
                                        <path fill="white" d="M19,11V9H11V5H9V9H5V11H9V19H11V11H19Z" />
                                        <rect fill="white" x="3" y="3" width="18" height="18" rx="2" fillOpacity="0.3" />
                                    </svg>
                                    {isExporting ? 'Exportant...' : 'Exportar a Google Sheets'}
                                </button>
                                {exportUrl && (
                                    <>
                                        <button 
                                            className="export-button manual-open" 
                                            onClick={handleManualOpen}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" className="sheets-icon">
                                                <path fill="white" d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                                            </svg>
                                            Obrir Google Sheets
                                        </button>
                                        <button 
                                            className="export-button copy-url" 
                                            onClick={handleCopyUrl}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" className="sheets-icon">
                                                <path fill="white" d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
                                            </svg>
                                            {copySuccess ? 'Enllaç copiat!' : 'Copiar enllaç'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {proves.length === 0 ? (
                        <div className="no-proves-message">
                            <p>No hi ha cap prova posada per aquest esdeveniment.</p>
                        </div>
                    ) : null}

                    {proves.map((prova, provaIndex) => {
                        const bundleInfo = bundlesInfo?.find(b => b.id === prova.castell);
                        const bundleJSON = bundleJSONs[prova.castell];
                        const currentTronc = troncs[prova.event]?.[prova.versio];

                        if (!currentTronc) {
                            return (
                                <div key={`${prova.castell}-${prova.versio}`} className="tronc-section">
                                    <div className="tronc-header">
                                        <ProvaTitle
                                            bundleInfo={bundleInfo}
                                            hores={provesHores?.[provaIndex]}
                                            {...prova}
                                        />
                                    </div>
                                    <TroncSkeleton />
                                </div>
                            );
                        }

                        const pomsStatus = {
                            dos: currentTronc?.some(t => t.some(([cx]) => bundleJSON?.[cx]?.pom === 'dos')),
                            acotxador: currentTronc?.some(t => t.some(([cx]) => bundleJSON?.[cx]?.pom === 'acotxador')),
                            enxaneta: currentTronc?.some(t => t.some(([cx]) => bundleJSON?.[cx]?.pom === 'enxaneta')),
                        };

                        const isSimultani = Object.keys(bundleJSON || {})?.[0]?.includes('_');
                        const hasPisos = currentTronc?.map(t => t.length).reduce((a, b) => Math.max(a, b), 0) > 0;

                        return (
                            <div key={`${prova.castell}-${prova.versio}`} className="tronc-section">
                                <div className="tronc-header">
                                    <ProvaTitle
                                        bundleInfo={bundleInfo}
                                        hores={provesHores?.[provaIndex]}
                                        {...prova}
                                    />
                                </div>

                                {hasPisos && (
                                    <div className="tronc-content">
                                        <div className="position-labels">
                                            {pomsStatus.enxaneta && (
                                                <div className="position-label pom">Enxaneta</div>
                                            )}
                                            {pomsStatus.acotxador && (
                                                <div className="position-label pom">Acotxador</div>
                                            )}
                                            {pomsStatus.dos && (
                                                <div className="position-label pom">Dosos</div>
                                            )}
                                            {(() => {
                                                const numPisosNoPoms = currentTronc
                                                    ?.map(t => t.filter(([cx]) => !bundleJSON?.[cx]?.pom).length)
                                                    .reduce((a, b) => Math.max(a, b), 0)
                                                    - 2 // column and fustes

                                                const hasBaixos = currentTronc
                                                    ?.map(t => t.map(([cx]) => bundleJSON?.[cx]?.pestanya))
                                                    .flat()
                                                    .some(pestanya => pestanya === 'pinya');

                                                const pisosLabels = ['Setens', 'Sisens', 'Quints', 'Quarts', 'Terços', 'Segons'];
                                                if (hasBaixos) {
                                                    pisosLabels.push('Baixos');
                                                }
                                                const startIndex = Math.max(0, pisosLabels.length - numPisosNoPoms);

                                                return pisosLabels
                                                    .slice(startIndex)
                                                    .map((label, index) => (
                                                        <div key={label} className="position-label">{label}</div>
                                                    ));
                                            })()}
                                            <div className="position-label">Fustes</div>
                                            <div className="position-label"></div>
                                        </div>

                                        <div className="rengles-container">
                                            {[...currentTronc].map((rengla, i) => (
                                                <Rengla
                                                    key={i}
                                                    tronc={currentTronc}
                                                    pomsStatus={pomsStatus}
                                                    renglaNum={currentTronc.length - 1 - i}
                                                    rengla={rengla}
                                                    castellersInfo={castellersInfo}
                                                    selectBox={(box) => () => handleBoxSelect(prova, box)}
                                                    selectedBox={selectedBoxes[prova.versio]}
                                                    assistencies={assistencies}
                                                    hores={provesHores?.[provaIndex]}
                                                    bundleJSON={bundleJSON}
                                                    allFustes={[]}
                                                    lastPair={lastPairs[prova.versio]}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <MissatgesProva
                                    castellersInfo={castellersInfo}
                                    event={event}
                                    castell={prova.castell}
                                    versio={prova.versio}
                                    socket={socket}
                                    userInfo={userInfo}
                                />
                            </div>
                        );
                    })}
                </div>

                <CastellersList
                    assistencies={assistencies}
                    selectedTronc={selectedTronc}
                    currentCastellersInTronc={currentCastellersInTronc}
                    hasCastellerInSelectedBox={hasCastellerInSelectedBox}
                    canSwap={canSwap}
                    onCastellerSelect={handleCastellerSelect}
                    onSwap={handleSwap}
                    selectedCasteller={selectedCasteller}
                    selectedBox={selectedBoxes[selectedTronc]}
                    etiquetes={etiquetes}
                    etiquetaUsers={etiquetaUsers}
                />
            </div>

            <style jsx>{`
                .web-app-troncs {
                    display: flex;
                    flex-direction: column;
                    height: 92%;
                    padding: 1rem;
                }

                .web-app-troncs.mobile {
                    padding: 0.5rem;
                    height: 100%;
                }

                .event-info {
                    margin-bottom: 2rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .event-info-content {
                    flex: 1;
                    min-width: 250px;
                }

                .event-info h1 {
                    margin: 0 0 0.5rem 0;
                    font-size: 1.8rem;
                }

                .event-datetime {
                    color: #666;
                    font-size: 1.1rem;
                    margin-bottom: 0.25rem;
                }

                .event-location {
                    color: #666;
                    font-size: 1rem;
                }

                .event-buttons {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                    align-items: center;
                }

                .troncs-container {
                    display: flex;
                    gap: 2rem;
                    flex: 1;
                    overflow: hidden;
                    height: calc(100vh - 250px);
                    min-height: 300px;
                }

                .troncs-list {
                    flex: 1;
                    overflow-y: auto;
                    padding-right: 1rem;
                    min-width: 0;
                    height: 60vh;
                }

                .tronc-section {
                    background: #f5f5f5;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                }

                .no-proves-message {
                    background: #f0f4f8;
                    border: 1px dashed #a0aec0;
                    border-radius: 8px;
                    padding: 2rem;
                    text-align: center;
                    color: #4a5568;
                    font-size: 1.1rem;
                }

                .no-proves-message p {
                    margin: 0;
                }

                .tronc-content {
                    display: flex;
                    margin-top: 1rem;
                    min-height: 200px;
                    margin-bottom: 0;
                }

                .position-labels {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    gap: 8px;
                    padding: 0.25rem;
                    min-width: 70px;
                    margin-bottom: 10px;
                }

                .position-label {
                    height: 35px;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    font-size: 0.7rem;
                    color: #666;
                    padding-right: 0.5rem;
                    margin: 3px 0;
                }

                .position-label:not(:last-child) {
                    margin-bottom: 3px;
                }

                .position-label.pom {
                    margin-bottom: 3px;
                }

                .rengles-container {
                    display: flex;
                    gap: 1rem;
                    overflow-x: auto;
                    padding-bottom: 1rem;
                    align-items: flex-end;
                    flex: 1;
                }

                .download-button {
                    margin-top: 1rem;
                    padding: 0.5rem 1rem;
                    background-color: #4CAF50;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 1rem;
                    transition: background-color 0.2s;
                }

                .download-button:hover {
                    background-color: #45a049;
                }
                
                .export-button {
                    padding: 0.5rem 1rem;
                    background-color: #4CAF50;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 1rem;
                    transition: background-color 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    white-space: nowrap;
                    min-width: 160px;
                }
                
                .sheets-icon {
                    margin-right: 4px;
                    flex-shrink: 0;
                }
                
                .export-button:hover {
                    background-color: #45a049;
                }
                
                .export-button:disabled {
                    background-color: #ccc;
                    cursor: not-allowed;
                }
                
                .export-button.manual-open {
                    background-color: #4285F4;
                }
                
                .export-button.manual-open:hover {
                    background-color: #3367D6;
                }
                
                .export-button.copy-url {
                    background-color: #673AB7;
                }
                
                .export-button.copy-url:hover {
                    background-color: #5E35B1;
                }

                /* Mobile styles */
                @media (max-width: 768px) {
                    .web-app-troncs {
                        padding: 0.5rem;
                    }

                    .event-info {
                        flex-direction: column;
                        margin-bottom: 1rem;
                    }

                    .event-info h1 {
                        font-size: 1.4rem;
                    }

                    .event-buttons {
                        margin-top: 0.5rem;
                        width: 100%;
                        justify-content: center;
                    }

                    .export-button {
                        font-size: 0.8rem;
                        padding: 0.4rem 0.8rem;
                        min-width: 140px;
                    }
                }
            `}</style>
        </div>
    );
}

export default React.memo(WebAppTroncs);
